import { useTranslation } from "react-i18next";
import { PlayerPositionsShortEnum } from "../enums/entity.enum";
import { formatImgUrl } from "../utils/cardFormating";
import {
  getAgentProfileRoute,
  getCoachProfileRoute,
  getEntityProfileRoute,
  getOtherProfileRoute,
  getPlayerProfileRoute,
  getScoutProfileRoute,
} from "../routers/routeUtils";
import { Country, OverviewCard, PlayerPositions, PlayerPositionsShorts, UserInfo } from "../types";

export const useDataFormatter = (data: UserInfo | undefined) => {
  const { t } = useTranslation();

  let fullName: string = "",
    firstTwoChr: string = "",
    club: string = "",
    about: string = "",
    countryName: string = "",
    countryMedia: string = "",
    cityName: string = "",
    media: string = "",
    mediaFileName: string = "",
    role: string = "",
    verificationStatus: string = "",
    isVerified: boolean = false,
    profileViewers: number = 0,
    posts: number = 0,
    followersCount: number = 0,
    followingCount: number = 0,
    id: number | null = null,
    entityId: number | null = null,
    isFollowing: boolean = false,
    profileLink: string = "",
    mutualFollowersCount: number = 0,
    countryFlag: string = "",
    playerPositionShort: PlayerPositionsShorts | null = null,
    friendsCount: number | null = null,
    isManagedByEntity: boolean = false,
    entityLevel: string | null = null,
    overview: OverviewCard[] = [],
    primaryPosition: PlayerPositions | string = "",
    otherPosition: PlayerPositions | string = "",
    countriesWorkedIn: Country[] = [],
    email: string = "";

  if (!data) {
    return {
      fullName: "",
      firstTwoChr: "",
      about: "",
      club: "",
      countryName: "",
      countryMedia: "",
      cityName: "",
      media: undefined,
      mediaFileName: "",
      role: "",
      profileViewers: 0,
      verificationStatus: "",
      isVerified: false,
      posts: 0,
      followersCount: 0,
      followingCount: 0,
      id: null,
      entityId: null,
      isFollowing: false,
      profileLink: "",
      mutualFollowersCount: 0,
      countryFlag: "",
      playerPositionShort: "",
      friendsCount: null,
      isManagedByEntity: false,
      entityLevel: null,
      overview: [],
      primaryPosition: "",
      otherPosition: "",
      countriesWorkedIn: [],
      email: "",
    };
  }

  if (data.entity) {
    fullName = data.entity.name;
    firstTwoChr = data.entity.name.trim().slice(0, 2).toLocaleUpperCase();
    about = data.entity.about;
    countryName = data?.entity?.country?.name || "";
    countryMedia = formatImgUrl(data?.entity?.country?.url || "", true);
    cityName = data?.city || "";
    media = formatImgUrl(data.entity.mediaUrl);
    mediaFileName = data.entity.mediaUrl;
    // role = FootballEntity[data.entity.type];
    role = t(`enums.entity.entityType.${data.entity.type}`);
    verificationStatus = data.verificationStatus;
    isVerified = data.verificationStatus === "VERIFIED" || data.entity.status === "approved";
    profileViewers = data.entity.profileViewers || 0;
    posts = data.entity.posts || 0;
    followersCount = data.entity.followersCount;
    followingCount = data.entity.followingCount || 0;
    id = data.id;
    entityId = data.entity.id;
    isFollowing = data.isFollowing || false;
    profileLink = getEntityProfileRoute(data.entity!.id) || "";
    mutualFollowersCount = data.mutualFollowersCount || 0;
    countryFlag = formatImgUrl(data?.entity?.country?.url || "");
    isManagedByEntity = data.entity.isManagedByEntity;
    // entityLevel = data.entity.level;
    entityLevel = t(`enums.entity.entityLevel.${data.entity.level}`);
    email = data.entity.email || "";
  } else if (data.isEntity) {
    fullName = data.name || "";
    firstTwoChr = data.name?.trim().slice(0, 2).toLocaleUpperCase() || "";
    about = data.about || "";
    countryName = data.country.name;
    countryMedia = formatImgUrl(data.country.url, true);
    media = formatImgUrl(data.mediaUrl);
    mediaFileName = data.mediaUrl || "";
    // role = data.type ? FootballEntity[data.type as keyof typeof FootballEntity] : "";
    role = t(`enums.entity.entityType.${data.type}`);
    followersCount = data.followersCount;
    followingCount = data.followingCount || 0;
    id = data.userId || null;
    entityId = data.id || null;
    isFollowing = data.isFollowing || false;
    profileLink = getEntityProfileRoute(data.id) || "";
    mutualFollowersCount = data.mutualFollowersCount || 0;
    countryFlag = formatImgUrl(data.country.url);
    isManagedByEntity = data.isManagedByEntity || false;
    // entityLevel = (data.type as EntityLevel) || null;
    entityLevel = t(`enums.entity.entityLevel.${data.type}`);
    email = data.email || "";
  } else {
    fullName = `${data.firstName} ${data.lastName}`;
    firstTwoChr = `${data.firstName?.trim().slice(0, 1)}${data.lastName?.trim().slice(0, 1)}`.toLocaleUpperCase();
    about = data.bio;
    club = data.club || "";
    countryName = data?.country?.name;
    countryMedia = formatImgUrl(data?.country?.url, true);
    cityName = data?.city || "";
    media = data.media?.url ? formatImgUrl(data.media?.url) : "";
    mediaFileName = data.media?.url || "";
    role = data.role === "OTHER" ? t(`enums.persona.role.${data.subRole}`) : t(`enums.persona.role.${data.role}`);
    verificationStatus = data.verificationStatus;
    isVerified = data.verificationStatus === "VERIFIED";
    profileViewers = data.profileViewers || 0;
    posts = data.posts || 0;
    followersCount = data.followersCount;
    followingCount = data.followingCount || 0;
    id = data.id;
    entityId = null;
    isFollowing = data.isFollowing || false;
    profileLink =
      data.role === "PLAYER"
        ? getPlayerProfileRoute(data.id)
        : data.role === "AGENT"
        ? getAgentProfileRoute(data.id)
        : data.role === "SCOUT"
        ? getScoutProfileRoute(data.id)
        : data.role === "COACH"
        ? getCoachProfileRoute(data.id)
        : getOtherProfileRoute(data.id);
    mutualFollowersCount = data.mutualFollowersCount || 0;
    countryFlag = formatImgUrl(data?.country?.url);
    playerPositionShort = data.additionalProperties?.primaryPosition
      ? PlayerPositionsShortEnum[data.additionalProperties.primaryPosition] || ""
      : null;
    friendsCount = data?.friendsCount && data.friendsCount > 0 ? data.friendsCount : null;
    overview = data.overview || [];
    primaryPosition = data.primaryPosition || "";
    otherPosition = data.otherPosition || "";
    countriesWorkedIn = data.countriesWorkedIn || [];
    email = data.email || "";
  }
  return {
    fullName,
    firstTwoChr,
    about,
    club,
    countryName,
    countryMedia,
    cityName,
    media,
    mediaFileName,
    role,
    profileViewers,
    verificationStatus,
    isVerified,
    posts,
    followersCount,
    followingCount,
    id,
    entityId,
    isFollowing,
    profileLink,
    mutualFollowersCount,
    countryFlag,
    playerPositionShort,
    friendsCount,
    isManagedByEntity,
    entityLevel,
    overview,
    primaryPosition,
    otherPosition,
    countriesWorkedIn,
    email,
  };
};
